<template>
  <div class="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center px-4 sm:px-6 lg:px-8">
    <div class="max-w-md w-full space-y-8">
      <!-- Header -->
      <div class="text-center">
        <h2 class="mt-6 text-3xl font-extrabold text-white">
          Reset Your Password
        </h2>
        <p class="mt-2 text-sm text-gray-300">
          Enter your new password below
        </p>
      </div>

      <!-- Reset Form -->
      <div class="bg-white/10 backdrop-blur-lg rounded-2xl shadow-2xl p-8 border border-white/20">
        <form @submit.prevent="handleResetPassword" class="space-y-6">
          <!-- New Password -->
          <div>
            <label for="newPassword" class="block text-sm font-medium text-white mb-2">
              New Password
            </label>
            <div class="relative">
              <input
                id="newPassword"
                v-model="form.newPassword"
                :type="showNewPassword ? 'text' : 'password'"
                @blur="validateNewPasswordOnBlur"
                required
                class="w-full px-4 py-3 bg-white/20 border border-white/30 rounded-lg text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
                placeholder="Enter your new password"
                :class="{ 'border-red-500': errors.newPassword }"
              />
              <button
                type="button"
                @click="showNewPassword = !showNewPassword"
                class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-300 hover:text-white transition-colors"
              >
                <svg v-if="showNewPassword" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"></path>
                </svg>
                <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                </svg>
              </button>
            </div>
            <p v-if="errors.newPassword" class="mt-1 text-sm text-red-400">
              {{ errors.newPassword }}
            </p>
          </div>

          <!-- Confirm Password -->
          <div>
            <label for="confirmPassword" class="block text-sm font-medium text-white mb-2">
              Confirm New Password
            </label>
            <div class="relative">
              <input
                id="confirmPassword"
                v-model="form.confirmPassword"
                :type="showConfirmPassword ? 'text' : 'password'"
                required
                class="w-full px-4 py-3 bg-white/20 border border-white/30 rounded-lg text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
                placeholder="Confirm your new password"
                :class="{ 'border-red-500': errors.confirmPassword }"
              />
              <button
                type="button"
                @click="showConfirmPassword = !showConfirmPassword"
                class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-300 hover:text-white transition-colors"
              >
                <svg v-if="showConfirmPassword" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"></path>
                </svg>
                <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                </svg>
              </button>
            </div>
            <p v-if="errors.confirmPassword" class="mt-1 text-sm text-red-400">
              {{ errors.confirmPassword }}
            </p>
          </div>

          <!-- General Error -->
          <div v-if="generalError" class="bg-red-500/20 border border-red-500/50 rounded-lg p-4">
            <p class="text-red-200 text-sm">{{ generalError }}</p>
          </div>

          <!-- Success Message -->
          <div v-if="successMessage" class="bg-green-500/20 border border-green-500/50 rounded-lg p-4">
            <p class="text-green-200 text-sm">{{ successMessage }}</p>
          </div>

          <!-- Submit Button -->
          <button
            type="submit"
            :disabled="loading || !isFormValid"
            class="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
          >
            <svg v-if="loading" class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            {{ loading ? 'Resetting Password...' : 'Reset Password' }}
          </button>

          <!-- Back to Login -->
          <div class="text-center">
            <router-link
              to="/"
              class="text-sm text-gray-300 hover:text-white transition-colors"
            >
              ← Back to Login
            </router-link>
          </div>
        </form>
      </div>

      <!-- Token Status -->
      <div v-if="tokenStatus" class="text-center">
        <p class="text-sm text-gray-300">
          {{ tokenStatus }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { apiCall } from '../config/api'

export default {
  name: 'ResetPassword',
  data() {
    return {
      form: {
        newPassword: '',
        confirmPassword: ''
      },
      errors: {
        newPassword: '',
        confirmPassword: ''
      },
      generalError: '',
      successMessage: '',
      loading: false,
      showNewPassword: false,
      showConfirmPassword: false,
      token: null,
      tokenStatus: ''
    }
  },
  computed: {
    isFormValid() {
      return this.form.newPassword && 
             this.form.confirmPassword && 
             this.form.newPassword === this.form.confirmPassword &&
             this.form.newPassword.length >= 8
    }
  },
  mounted() {
    this.extractTokenFromUrl()
    this.validateToken()
  },
  methods: {
    extractTokenFromUrl() {
      const urlParams = new URLSearchParams(window.location.search)
      this.token = urlParams.get('token')
      
      if (!this.token) {
        this.generalError = 'Invalid or missing reset token. Please check your email for the correct link.'
        this.tokenStatus = '❌ No token found in URL'
      } else {
        this.tokenStatus = '✅ Token found, validating...'
      }
    },

    async validateToken() {
      if (!this.token) return

      try {
        // You can add a token validation API call here if needed
        // For now, we'll just check if the token exists and has the right format
        if (this.token.length < 32) {
          this.generalError = 'Invalid token format. Please check your email for the correct link.'
          this.tokenStatus = '❌ Invalid token format'
          return
        }

        this.tokenStatus = '✅ Token appears valid'
      } catch (error) {
        console.error('Token validation error:', error)
        this.generalError = 'Error validating token. Please try again or request a new reset link.'
        this.tokenStatus = '❌ Token validation failed'
      }
    },

    validateNewPasswordOnBlur() {
      if (this.form.newPassword && this.form.newPassword.length < 8) {
        this.errors.newPassword = 'Password must be at least 8 characters long'
      } else if (this.errors.newPassword === 'Password must be at least 8 characters long') {
        this.errors.newPassword = ''
      }
    },

    validateForm() {
      this.errors = {
        newPassword: '',
        confirmPassword: ''
      }

      let isValid = true

      // Validate new password
      if (!this.form.newPassword) {
        this.errors.newPassword = 'New password is required'
        isValid = false
      } else if (this.form.newPassword.length < 8) {
        this.errors.newPassword = 'Password must be at least 8 characters long'
        isValid = false
      }

      // Validate confirm password
      if (!this.form.confirmPassword) {
        this.errors.confirmPassword = 'Please confirm your new password'
        isValid = false
      } else if (this.form.newPassword !== this.form.confirmPassword) {
        this.errors.confirmPassword = 'Passwords do not match'
        isValid = false
      }

      return isValid
    },

    async handleResetPassword() {
      if (!this.validateForm() || !this.token) {
        return
      }

      this.loading = true
      this.generalError = ''
      this.successMessage = ''

      try {
        const { response, data } = await apiCall('auth/password-reset/confirm', {
          method: 'POST',
          body: JSON.stringify({
            token: this.token,
            newPassword: this.form.newPassword
          })
        })

        if (response.ok && data.success) {
          this.successMessage = data.message || 'Password has been successfully reset!'
          this.tokenStatus = '✅ Password reset successful'
          
          // Clear form
          this.form.newPassword = ''
          this.form.confirmPassword = ''
          
          // Redirect to home page after 3 seconds
          setTimeout(() => {
            this.$router.push('/')
          }, 3000)
        } else {
          this.generalError = data.message || 'Failed to reset password. Please try again.'
          this.tokenStatus = '❌ Password reset failed'
        }
      } catch (error) {
        console.error('Reset password error:', error)
        this.generalError = 'Network error. Please check your connection and try again.'
        this.tokenStatus = '❌ Network error'
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
/* Custom scrollbar for webkit browsers */
::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.5);
}

/* Smooth transitions */
* {
  transition: all 0.2s ease-in-out;
}

/* Focus styles */
input:focus {
  box-shadow: 0 0 0 3px rgba(147, 51, 234, 0.1);
}

/* Button hover effects */
button:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
}
</style>
